import { DatePicker } from '@components/common/DatePicker';
import { useAppSelector } from '@hooks/useRedux';
import { selectAccommodationProperty } from '@store/accommodation';
import { Download, RefreshCw } from 'lucide-react';
import React, { useCallback, useMemo, useState } from 'react';
import {
  RoomRevenueMatrixBooking,
  RoomRevenueMatrixData,
  RoomRevenueMatrixRoom,
  fetchRoomRevenueMatrix,
} from '../../../../services/accommodationService';
import { apiError } from '../../../../util/notify';
import {
  currentMonthRange,
  exportToCsv,
  fmtAmount,
  fmtDate,
} from './reportUtils';

const { from: initFrom, to: initTo } = currentMonthRange();

function buildDays(from: string, to: string): string[] {
  const days: string[] = [];
  const end = new Date(to);
  for (let d = new Date(from); d <= end; d.setDate(d.getDate() + 1)) {
    days.push(d.toISOString().split('T')[0]);
  }
  return days;
}

function dayLabel(iso: string): { day: string; month: string } {
  const d = new Date(iso);
  return {
    day: String(d.getDate()).padStart(2, '0'),
    month: d.toLocaleString('en', { month: 'short' }),
  };
}

interface RoomGroup {
  typeId: string;
  typeName: string;
  typeSort: number;
  rooms: RoomRevenueMatrixRoom[];
}

function groupRooms(rooms: RoomRevenueMatrixRoom[]): RoomGroup[] {
  const map = rooms.reduce<Map<string, RoomGroup>>((acc, r) => {
    if (!acc.has(r.room_type_id)) {
      acc.set(r.room_type_id, {
        typeId: r.room_type_id,
        typeName: r.room_type_name,
        typeSort: r.room_type_sort,
        rooms: [],
      });
    }
    acc.get(r.room_type_id)!.rooms.push(r);
    return acc;
  }, new Map<string, RoomGroup>());
  return Array.from(map.values()).sort((a, b) =>
    a.typeName.localeCompare(b.typeName),
  );
}

function buildMatrix(
  bookings: RoomRevenueMatrixBooking[],
  days: string[],
): Map<string, Map<string, number>> {
  const daySet = new Set(days);

  return bookings.reduce<Map<string, Map<string, number>>>((matrix, b) => {
    const checkIn = b.check_in.split('T')[0];
    const checkOut = b.check_out.split('T')[0];

    buildDays(checkIn, checkOut)
      .filter((dateStr) => dateStr < checkOut && daySet.has(dateStr))
      .forEach((dateStr) => {
        if (!matrix.has(b.room_id)) matrix.set(b.room_id, new Map());
        const prev = matrix.get(b.room_id)!.get(dateStr) ?? 0;
        matrix.get(b.room_id)!.set(dateStr, prev + b.daily_rate);
      });

    return matrix;
  }, new Map<string, Map<string, number>>());
}

const CELL_BASE =
  'h-9 px-2 text-right text-xs tabular-nums border-b border-gray-100';
const CELL_BOOKED = `${CELL_BASE} font-medium text-gray-900 bg-green-50`;
const CELL_EMPTY = `${CELL_BASE} text-gray-300`;
const STICKY_HEADER =
  'sticky left-0 z-10 bg-white border-r border-gray-200 whitespace-nowrap';
const STICKY_TOTAL =
  'sticky right-0 z-10 bg-white border-l border-gray-200 whitespace-nowrap';

export const RoomRevenueReportPage: React.FC = () => {
  const property = useAppSelector(selectAccommodationProperty);

  const [dateFrom, setDateFrom] = useState(initFrom);
  const [dateTo, setDateTo] = useState(initTo);
  const [matrixData, setMatrixData] = useState<RoomRevenueMatrixData>({
    rooms: [],
    bookings: [],
  });
  const [isLoading, setIsLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);

  const load = useCallback(async () => {
    if (!property) {
      apiError(
        'Please select a property before running the room revenue report.',
      );
      return;
    }
    setIsLoading(true);
    try {
      const data = await fetchRoomRevenueMatrix(property.id, dateFrom, dateTo);
      setMatrixData(data);
      setLoaded(true);
    } catch (err) {
      apiError(err instanceof Error ? err.message : 'Failed to load report');
    } finally {
      setIsLoading(false);
    }
  }, [property, dateFrom, dateTo]);

  const days = useMemo(() => buildDays(dateFrom, dateTo), [dateFrom, dateTo]);
  const groups = useMemo(
    () => groupRooms(matrixData.rooms),
    [matrixData.rooms],
  );
  const matrix = useMemo(
    () => buildMatrix(matrixData.bookings, days),
    [matrixData.bookings, days],
  );
  const currency = property?.currency ?? '';

  const roomTotals = useMemo(() => {
    const totals = new Map<string, number>();
    Array.from(matrix.entries()).forEach(([roomId, dayMap]) => {
      totals.set(
        roomId,
        Array.from(dayMap.values()).reduce((sum, value) => sum + value, 0),
      );
    });
    return totals;
  }, [matrix]);

  const dayTotals = useMemo(() => {
    const totals = new Map<string, number>();
    Array.from(matrix.values()).forEach((dayMap) => {
      Array.from(dayMap.entries()).forEach(([date, amount]) => {
        totals.set(date, (totals.get(date) ?? 0) + amount);
      });
    });
    return totals;
  }, [matrix]);

  const grandTotal = useMemo(
    () => Array.from(dayTotals.values()).reduce((s, v) => s + v, 0),
    [dayTotals],
  );

  const handleCsv = () => {
    const csvRows: Record<string, unknown>[] = groups.flatMap((g) =>
      g.rooms.map((room) => {
        const row: Record<string, unknown> = {
          'Room Type': g.typeName,
          Room: room.room_number,
        };
        days.forEach((day) => {
          const amount = matrix.get(room.room_id)?.get(day);
          row[day] = amount ? fmtAmount(amount) : '';
        });
        row['Period Total'] = fmtAmount(roomTotals.get(room.room_id) ?? 0);
        return row;
      }),
    );
    const totalRow: Record<string, unknown> = {
      'Room Type': 'TOTAL',
      Room: '',
      'Period Total': fmtAmount(grandTotal),
    };
    days.forEach((day) => {
      const total = dayTotals.get(day);
      totalRow[day] = total ? fmtAmount(total) : '';
    });
    csvRows.push(totalRow);
    exportToCsv(`room-revenue-${dateFrom}-${dateTo}`, csvRows);
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end gap-3 rounded-2xl border border-gray-200 bg-white p-4">
        <DatePicker
          mode="single"
          id="rr-date-from"
          label="From date"
          value={dateFrom}
          onChange={setDateFrom}
        />
        <DatePicker
          mode="single"
          id="rr-date-to"
          label="To date"
          value={dateTo}
          onChange={setDateTo}
        />
        <button
          type="button"
          onClick={load}
          disabled={isLoading}
          className="flex items-center gap-2 rounded-xl bg-pos-primary px-4 py-2 text-sm font-medium text-white hover:bg-pos-primary/90 disabled:opacity-60"
        >
          <RefreshCw size={14} className={isLoading ? 'animate-spin' : ''} />
          {isLoading ? 'Loading…' : 'Run report'}
        </button>
        {loaded && matrixData.rooms.length > 0 && (
          <button
            type="button"
            onClick={handleCsv}
            className="flex items-center gap-2 rounded-xl border border-gray-200 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            <Download size={14} />
            Export CSV
          </button>
        )}
      </div>

      {loaded && (
        <div className="grid gap-4 sm:grid-cols-3">
          {[
            {
              label: 'Rooms',
              value: String(matrixData.rooms.length),
              sub: 'active rooms',
            },
            { label: 'Days', value: String(days.length), sub: 'in period' },
            {
              label: 'Total Revenue',
              value: `${currency} ${fmtAmount(grandTotal)}`,
              sub: `${fmtDate(dateFrom)} – ${fmtDate(dateTo)}`,
            },
          ].map((c) => (
            <div
              key={c.label}
              className="rounded-2xl border border-gray-200 bg-white p-4"
            >
              <p className="text-xs text-gray-500">{c.label}</p>
              <p className="mt-1 text-xl font-semibold text-gray-900">
                {c.value}
              </p>
              <p className="mt-0.5 text-xs text-gray-400">{c.sub}</p>
            </div>
          ))}
        </div>
      )}

      {loaded && (
        <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white">
          <div className="border-b border-gray-100 px-5 py-3">
            <h4 className="text-sm font-semibold text-gray-900">
              Room revenue matrix — {fmtDate(dateFrom)} to {fmtDate(dateTo)}
            </h4>
          </div>

          {matrixData.rooms.length === 0 ? (
            <div className="flex h-28 items-center justify-center text-sm text-gray-400">
              No active rooms found for this property.
            </div>
          ) : (
            <div className="overflow-auto max-h-[420px]">
              <table
                className="border-collapse text-sm"
                style={{ minWidth: 'max-content' }}
              >
                <thead className="sticky top-0 z-20">
                  <tr className="bg-gray-50">
                    <th
                      className={`${STICKY_HEADER} bg-gray-50 px-3 py-2 text-left text-xs font-semibold text-gray-600`}
                      style={{ minWidth: 160 }}
                    >
                      Room
                    </th>
                    {days.map((day) => {
                      const { day: d, month: m } = dayLabel(day);
                      return (
                        <th
                          key={day}
                          className="min-w-[64px] border-b border-gray-200 px-2 py-1 text-center text-xs font-semibold text-gray-600"
                        >
                          <span className="block leading-none">{d}</span>
                          <span className="block text-[10px] font-normal text-gray-400">
                            {m}
                          </span>
                        </th>
                      );
                    })}
                    <th
                      className={`${STICKY_TOTAL} bg-gray-50 px-3 py-2 text-right text-xs font-semibold text-gray-600`}
                      style={{ minWidth: 176 }}
                    >
                      Period Total
                    </th>
                  </tr>
                </thead>

                <tbody>
                  {groups.map((g) => (
                    <React.Fragment key={g.typeId}>
                      <tr className="bg-gray-50">
                        <td
                          className={`${STICKY_HEADER} bg-gray-50 px-3 py-1.5 text-xs font-bold uppercase tracking-wide text-gray-500`}
                        >
                          {g.typeName}
                        </td>
                        {days.map((day) => (
                          <td
                            key={day}
                            aria-label={day}
                            className="border-b border-gray-100 bg-gray-50"
                          />
                        ))}
                        <td
                          className={`${STICKY_TOTAL} border-b border-gray-100 bg-gray-50 px-3 py-1.5 text-right text-[11px] font-bold uppercase tracking-wide text-gray-400`}
                        >
                          {g.typeName}
                        </td>
                      </tr>

                      {g.rooms.map((room) => (
                        <tr key={room.room_id} className="hover:bg-blue-50/30">
                          <td
                            className={`${STICKY_HEADER} px-3 py-2 text-xs font-medium text-gray-800`}
                          >
                            Room {room.room_number}
                          </td>
                          {days.map((day) => {
                            const amount = matrix.get(room.room_id)?.get(day);
                            return (
                              <td
                                key={day}
                                className={amount ? CELL_BOOKED : CELL_EMPTY}
                              >
                                {amount ? fmtAmount(amount) : '·'}
                              </td>
                            );
                          })}
                          <td
                            className={`${STICKY_TOTAL} h-9 border-b border-gray-100 px-3 text-right text-xs font-semibold tabular-nums text-gray-900`}
                          >
                            {fmtAmount(roomTotals.get(room.room_id) ?? 0)}
                          </td>
                        </tr>
                      ))}
                    </React.Fragment>
                  ))}
                </tbody>

                <tfoot className="sticky bottom-0 z-20">
                  <tr className="border-t-2 border-gray-200 bg-gray-50">
                    <td
                      className={`${STICKY_HEADER} bg-gray-50 px-3 py-2.5 text-xs font-bold uppercase text-gray-700`}
                    >
                      Daily Total
                    </td>
                    {days.map((day) => {
                      const total = dayTotals.get(day);
                      return (
                        <td
                          key={day}
                          className="border-b border-gray-200 px-2 py-2.5 text-right text-xs font-bold text-gray-900 tabular-nums"
                        >
                          {total ? fmtAmount(total) : '—'}
                        </td>
                      );
                    })}
                    <td
                      className={`${STICKY_TOTAL} border-b border-gray-200 bg-gray-50 px-3 py-2.5 text-right text-xs font-bold uppercase text-gray-900`}
                    >
                      {currency} {fmtAmount(grandTotal)}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </div>
      )}

      {!loaded && !isLoading && (
        <div className="flex h-40 items-center justify-center rounded-2xl border border-dashed border-gray-200 text-sm text-gray-400">
          Select a date range and click &ldquo;Run report&rdquo; to load data.
        </div>
      )}
    </div>
  );
};
